import { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SideNav } from './sideNav';
import { Header } from './header';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';

import { getAmbientSoloRoutes } from 'src/modules/ambient_solo/Routes';

import { getSuperAdminRoutes } from 'src/modules/SuperAdmin/Routes';
import { getTemplatesExchangeRoutes } from 'src/modules/TemplatesExchange/Routes';
import { getCelloWidgetRoutes } from 'src/modules/CelloWidget/Routes';
import { getCareConnectRoutes } from 'src/modules/appointments/Routes';
import { getSettingsRoutes } from 'src/modules/appointments/SettingsRoutes';
import { useAppRoutes } from 'src/pages';
import { DemoContainer } from 'src/pages/root';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import styled from '@emotion/styled';
import { ScreenLoader } from 'src/components';
import { MobileNav } from './mobileNav';
import { useScreenSize } from '@insight-health-ai/roseate/utils';
import { analytics } from 'src/services';
import { useCello } from 'src/hooks/misc';

const LayoutWrapper = styled.div<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    isMobile
      ? `
      --page-wrapper-content-height: calc(100dvh - 92px - 80px);
    `
      : `
      --page-wrapper-content-height: calc(100dvh - 92px);
    `}
  background-image: linear-gradient(
    to right,
    #e8f8ff,
    #e7f8ff,
    #e8f8ff,
    #ebf7ff,
    #e7f6ff,
    #eff7fd,
    #edf8ff
  );
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  height: 100dvh;
  width: 100dvw;
`;

export const Layout = () => {
  const { user } = useAuth();
  const { mediaMax } = useScreenSize();
  const { isSuperAdmin } = useGetCurrentPractitioner();
  const { isEntitled: hasAmbientAccess } = useFeatureEntitlements('ambient');
  const { isEntitled: hasIntakeAccess } = useFeatureEntitlements('intake');
  const { isEntitled: hasFollowupAccess } = useFeatureEntitlements('followup');
  const { isEntitled: isPatientsModuleEntitled } =
    useFeatureEntitlements('patients-module');
  const { isEntitled: isFrontdeskModuleEntitled } =
    useFeatureEntitlements('frontdesk');
  const { isEntitled: isReferralsModuleEntitled } =
    useFeatureEntitlements('referral');
  const { isEntitled: isPhoneTriageModuleEntitled } =
    useFeatureEntitlements('phone-triage');
  const { isEntitled: isAppointmentListV2ModuleEntitled } =
    useFeatureEntitlements('appointment-list-v2');
  const routes = useAppRoutes();

  const { token: celloToken } = useCello();

  const getIndexRoute = () => {
    if (isPhoneTriageModuleEntitled) {
      return '/phone-triage';
    }
    if (isReferralsModuleEntitled) {
      return '/referrals';
    }
    if (hasIntakeAccess || hasFollowupAccess) {
      return '/appointments';
    }
    if (hasAmbientAccess) {
      return '/encounters';
    }
    if (isFrontdeskModuleEntitled) {
      return '/frontdesk';
    }
    if (isPatientsModuleEntitled) {
      return '/patients';
    }
    return '/settings';
  };

  const isMobile = mediaMax('sm');

  useEffect(() => {
    analytics.track('Using Layout V2', {
      email: user?.email,
    });
  }, []);

  return (
    <LayoutWrapper
      isMobile={isMobile}
      className={`flex ${isMobile ? 'flex-col' : 'flex-row'} h-full w-full overflow-hidden`}
    >
      {!isMobile && <SideNav />}
      <div className="flex-shrink-1 flex flex-col h-full w-full overflow-hidden">
        <Header />
        <div className="flex-1 w-full p-4 overflow-y-auto">
          <Suspense fallback={<ScreenLoader />}>
            <Routes>
              {(hasIntakeAccess || hasFollowupAccess) &&
                getCareConnectRoutes({ v2: isAppointmentListV2ModuleEntitled })}
              {hasAmbientAccess &&
                getAmbientSoloRoutes({
                  isMinimized: true,
                  setIsMinimized: () => {},
                })}
              {isSuperAdmin && getSuperAdminRoutes()}
              {getSettingsRoutes()}
              {getTemplatesExchangeRoutes()}
              {celloToken && getCelloWidgetRoutes()}
              {routes}
              <Route
                path="*"
                element={<Navigate replace to={getIndexRoute()} />}
              />
            </Routes>
            <DemoContainer />
          </Suspense>
        </div>
      </div>
      {isMobile && <MobileNav />}
    </LayoutWrapper>
  );
};
